import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { authAPI } from '../services/api';
import LanguageToggle, { getLang, setLang } from './LanguageToggle';
import './Login.css';

const S = {
  en: {
    subtitle:   'Sign in to your account',
    user_label: 'Username',
    user_ph:    'Enter your username',
    pass_label: 'Password',
    pass_ph:    'Enter your password',
    signing_in: 'Signing in…',
    sign_in:    'Sign In',
    or:         'Or continue with',
    telus_btn:  'Login with @telus Email',
    telusmd_btn:'Login with @telusmd Email',
    telus_note: 'For TELUS Health employees with a valid TELUS email address.',
  },
  fr: {
    subtitle:   'Connectez-vous à votre compte',
    user_label: "Nom d'utilisateur",
    user_ph:    "Entrez votre nom d'utilisateur",
    pass_label: 'Mot de passe',
    pass_ph:    'Entrez votre mot de passe',
    signing_in: 'Connexion…',
    sign_in:    'Se connecter',
    or:         'Ou continuer avec',
    telus_btn:  'Se connecter avec son adresse courriel @telus',
    telusmd_btn:'Se connecter avec son adresse courriel @telusmd',
    telus_note: 'Pour les employés TELUS Santé avec une adresse email TELUS valide.',
  },
};

export default function Login({ onAuthenticated } = {}) {
  const [username,     setUsername]     = useState('');
  const [password,     setPassword]     = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error,        setError]        = useState('');
  const [loading,      setLoading]      = useState(false);
  const [language,     setLanguage]     = useState(getLang);
  const navigate = useNavigate();

  const s = S[language];

  const toggleLanguage = () => {
    const next = language === 'en' ? 'fr' : 'en';
    setLanguage(next);
    setLang(next);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await authAPI.login(username, password);
      if (typeof onAuthenticated === 'function') onAuthenticated();
      navigate('/chat');
    } catch (err) {
      setError(err.response?.data?.detail || err.message || 'Login failed. Please check your credentials.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-page">

      {/* EN/FR toggle — top-left */}
      <div className="login-lang-bar">
        <LanguageToggle language={language} onToggle={toggleLanguage} />
      </div>

      {/* TELUS Health logo — top center */}
      <div className="login-logo-bar">
        <img
          src={language === 'fr' ? '/TELUS_Sante_Cliniques_FR_Vert_2021_Digital_RGB.svg' : '/TELUS_Health_CareCentres_EN_Vert_2021_Digital_RGB.svg'}
          alt={language === 'fr' ? 'TELUS Santé' : 'TELUS Health'}
          className="login-logo"
        />
      </div>

      {/* Left: sweet pea illustration, bottom-anchored */}
      <div className="login-left" aria-hidden="true">
        <img
          src="/SweetPea_Purple_SideviewStem_BH_LR_RGB_0.jpg"
          alt=""
          className="login-iris"
        />
      </div>

      {/* Right: login form */}
      <div className="login-right">
        <div className="login-card">

          <h1 className="login-title">PRISM</h1>
          <p className="login-subtitle">{s.subtitle}</p>

          <form onSubmit={handleSubmit} className="login-form" noValidate>
            {error && <div className="login-error">{error}</div>}

            <div className="login-field">
              <label htmlFor="username">{s.user_label}</label>
              <input
                type="text"
                id="username"
                value={username}
                onChange={e => setUsername(e.target.value)}
                placeholder={s.user_ph}
                required
                autoFocus
                autoComplete="username"
              />
            </div>

            <div className="login-field">
              <label htmlFor="password">{s.pass_label}</label>
              <div className="pass-wrapper">
                <input
                  type={showPassword ? 'text' : 'password'}
                  id="password"
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  placeholder={s.pass_ph}
                  required
                  autoComplete="current-password"
                />
                <button
                  type="button"
                  className="pass-toggle"
                  onClick={() => setShowPassword(v => !v)}
                  tabIndex={-1}
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                >
                  {showPassword ? '🙈' : '👁'}
                </button>
              </div>
            </div>

            <button type="submit" className="btn-sign-in" disabled={loading}>
              {loading ? s.signing_in : s.sign_in}
            </button>
          </form>

          <div className="login-divider"><span>{s.or}</span></div>

          <button type="button" className="btn-telus-email">
            {s.telus_btn}
          </button>

          <button type="button" className="btn-telus-email btn-telusmd-email">
            {s.telusmd_btn}
          </button>

          <p className="login-note">{s.telus_note}</p>
        </div>
      </div>
    </div>
  );
}
